import { useEffect, useMemo, useState } from "react";

import Button from "@/components/ui/button/Button";
import { handleApiError } from "@/services/api";
import { flockReportService, type FlockWeeklyRow } from "@/services/flockReportService";

/**
 * Each flock, week by week, against its breed standard.
 *
 * The charts on this page have shown the shape since the curves landed, and a
 * shape is not a number. What a farmer argues from — with a feed salesman, a
 * breeder rep, or their own supervisor — is the week and the gap: "week 31,
 * four points under, and it has been under for six weeks". That row does not
 * exist on a chart.
 *
 * See docs/architecture/flock-weekly.md.
 */
export default function WeeklyPerformanceTable({
  flockId,
  isLayer,
}: {
  flockId: number;
  isLayer: boolean;
}) {
  const [rows, setRows] = useState<FlockWeeklyRow[] | null>(null);
  const [sources, setSources] = useState<Record<string, string>>({});
  const [onboarded, setOnboarded] = useState(false);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError("");
    flockReportService
      .getWeeklyPerformance(flockId)
      .then((r) => {
        if (cancelled) return;
        setRows(r.rows);
        setSources(r.curve_sources ?? {});
        setOnboarded(r.onboarded);
      })
      .catch((err) => {
        if (!cancelled) setError(handleApiError(err));
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [flockId]);

  const csv = useMemo(() => (rows ? toCsv(rows) : ""), [rows]);

  if (loading) {
    return <p className="mb-6 py-4 text-center text-sm text-gray-400">Loading weekly summary…</p>;
  }
  if (error) {
    return (
      <div className="mb-6 rounded-lg bg-error-50 px-4 py-3 text-sm text-error-700 dark:bg-error-500/15 dark:text-error-400">
        {error}
      </div>
    );
  }
  if (!rows || rows.length === 0) {
    return (
      <div className="mb-6 card p-8 text-center">
        <p className="text-sm text-gray-500 dark:text-gray-400">
          Nothing recorded against this flock yet.
        </p>
      </div>
    );
  }

  const hdpSource = sources.hdp;
  // Every marked week, INCLUDING the ones with nothing at all. Requiring
  // days_recorded > 0 put an asterisk on an empty week with no legend
  // explaining it.
  const anyPartial = rows.some((r) => r.days_recorded < 7);

  return (
    <div className="mb-6 card p-5 sm:p-6">
      <div className="mb-3 flex flex-wrap items-start justify-between gap-3">
        <div>
          <h3 className="text-base font-semibold text-gray-800 dark:text-white/90">
            Weekly summary
          </h3>
          <p className="mt-0.5 text-sm text-gray-500 dark:text-gray-400">
            Age weeks from placement, each read against the standard for that
            age.{" "}
            {standardNote(hdpSource)}
          </p>
        </div>
        <Button
          size="sm"
          variant="outline"
          onClick={() => downloadCsv(csv, `flock-${flockId}-weekly.csv`)}
        >
          Download CSV
        </Button>
      </div>

      {/* A flock entered mid-cycle: its pre-adoption deaths were never
          recorded, so the denominator behind every rate is too high, each HDP
          reads low, and the gap reads negative on a flock that may be on
          target — in the column this table exists for. Marked rather than
          suppressed: a farm that adopted mid-cycle is every farm using this
          app today. */}
      {onboarded && (
        <p className="mb-3 rounded-lg bg-warning-50 px-3 py-2 text-xs text-warning-700 dark:bg-warning-500/15 dark:text-warning-400">
          This flock was entered mid-cycle, so the bird counts below only
          include deaths recorded since then. Production and the gap may read
          lower than the shed actually achieved.
        </p>
      )}

      {/* Which columns can be trusted on a short week. A three-day week has a
          real HDP — a rate over the days that exist — and an egg TOTAL that is
          three sevenths of a week. Saying so once is cheaper than a reader
          concluding the flock collapsed. */}
      {anyPartial && (
        <p className="mb-3 text-xs text-gray-500 dark:text-gray-400">
          Weeks with fewer than seven days entered are marked. Their rates
          (production %, feed per bird) are still right — they are measured over
          the days that exist. The <em>totals</em> are not a full week.
        </p>
      )}

      <div className="overflow-x-auto">
        <table className="min-w-full text-sm">
          <thead className="border-b border-gray-200 text-left text-xs uppercase tracking-wide text-gray-500 dark:border-gray-800 dark:text-gray-400">
            <tr>
              <th className="px-3 py-2">Week</th>
              <th className="px-3 py-2">From</th>
              <th className="px-3 py-2 text-right">Days</th>
              <th className="px-3 py-2 text-right">Birds</th>
              <th className="px-3 py-2 text-right">Deaths</th>
              <th className="px-3 py-2 text-right">Mort %</th>
              {isLayer && <th className="px-3 py-2 text-right">Eggs</th>}
              {isLayer && <th className="px-3 py-2 text-right">Prod %</th>}
              {isLayer && <th className="px-3 py-2 text-right">Std %</th>}
              {isLayer && <th className="px-3 py-2 text-right">Gap</th>}
              <th className="px-3 py-2 text-right">Feed g</th>
              <th className="px-3 py-2 text-right">Std g</th>
              {isLayer && <th className="px-3 py-2 text-right">Egg wt</th>}
              <th className="px-3 py-2 text-right">Body wt</th>
              <th className="px-3 py-2 text-right">Std wt</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100 dark:divide-gray-800">
            {rows.map((r) => (
              <tr key={r.age_weeks} className="text-gray-700 dark:text-gray-300">
                <td className="px-3 py-2 font-medium text-gray-800 dark:text-white/90">
                  {r.age_weeks}
                </td>
                <td className="px-3 py-2">{r.from.slice(0, 10)}</td>
                <td
                  className={`px-3 py-2 text-right tabular-nums ${
                    r.days_recorded < 7 ? "text-warning-700 dark:text-warning-400" : ""
                  }`}
                >
                  {r.days_recorded}
                  {r.days_recorded < 7 && "*"}
                </td>
                <td className="px-3 py-2 text-right tabular-nums">
                  {r.opening_birds.toLocaleString("en-IN")}
                </td>
                <td className="px-3 py-2 text-right tabular-nums">
                  {r.deaths === null ? "—" : r.deaths}
                </td>
                <td className="px-3 py-2 text-right tabular-nums">{num(r.mortality_pct, 2)}</td>
                {isLayer && (
                  <td className="px-3 py-2 text-right tabular-nums">
                    {/* NULL is "nobody counted"; 0 is "counted, and there were
                        none" — the most alarming row a laying flock can have.
                        Gating on truthiness rendered the dash for both, so the
                        same row said "not measured" in Eggs and "0.0" in Prod
                        % with a −91 gap beside it. */}
                    {r.eggs === null ? "—" : r.eggs.toLocaleString("en-IN")}
                  </td>
                )}
                {isLayer && <td className="px-3 py-2 text-right tabular-nums">{num(r.hdp_pct, 1)}</td>}
                {isLayer && (
                  <td className="px-3 py-2 text-right tabular-nums">{num(r.std_hdp_pct, 1)}</td>
                )}
                {isLayer && <GapCell gap={r.hdp_gap} />}
                <td className="px-3 py-2 text-right tabular-nums">{num(r.feed_per_bird_g, 0)}</td>
                <td className="px-3 py-2 text-right tabular-nums">
                  {num(r.std_feed_per_bird_g, 0)}
                </td>
                {isLayer && (
                  <td className="px-3 py-2 text-right tabular-nums">{num(r.egg_weight_g, 1)}</td>
                )}
                <td className="px-3 py-2 text-right tabular-nums">{num(r.body_weight_g, 0)}</td>
                <td className="px-3 py-2 text-right tabular-nums">{num(r.std_body_weight_g, 0)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

/**
 * Which standard the Gap column is measured against.
 *
 * THREE states, not one. A flock benchmarked against the shipped generic
 * curve is not benchmarked against its own breed, and a Gap column that does
 * not say so invites an argument with a breeder rep about somebody else's
 * numbers — which is the worry recorded when the gram curves shipped without
 * defaults.
 */
function standardNote(source: string | undefined): string {
  switch (source) {
    case "breed":
      return "Measured against this breed's own curve.";
    case "farm":
      return "Measured against this farm's own curve, not a breed guide.";
    case "default":
      return "Measured against the shipped generic layer curve — import this breed's guide under Master Data → Breed standards for a like-for-like gap.";
    default:
      return "No production curve available, so there is no gap column to read. Add one under Master Data → Breed standards.";
  }
}

/**
 * The column the table exists for.
 *
 * THREE states, not two. A gap of null is "no standard to compare against" and
 * must not render as 0.0 — a flock with no curve for its breed is not a flock
 * exactly on target, and the whole point of the curve work was to stop
 * inventing a number where none exists.
 */
function GapCell({ gap }: { gap: number | null }) {
  if (gap === null) {
    return <td className="px-3 py-2 text-right text-gray-400">—</td>;
  }
  const behind = gap < -0.05;
  const ahead = gap > 0.05;
  return (
    <td
      className={`px-3 py-2 text-right font-medium tabular-nums ${
        behind
          ? "text-error-600 dark:text-error-400"
          : ahead
            ? "text-success-700 dark:text-success-400"
            : ""
      }`}
    >
      {ahead ? "+" : ""}
      {gap.toFixed(1)}
    </td>
  );
}

/** A measured figure, or a dash — never a zero standing in for "not measured". */
function num(v: number | null, decimals: number): string {
  return v === null ? "—" : v.toFixed(decimals);
}

export function toCsv(rows: FlockWeeklyRow[]): string {
  const head = [
    "age_weeks", "from", "to", "days_recorded", "opening_birds", "deaths", "mortality_pct",
    "eggs", "hdp_pct", "std_hdp_pct", "hdp_gap", "feed_per_bird_g", "std_feed_per_bird_g",
    "egg_weight_g", "std_egg_weight_g", "body_weight_g", "std_body_weight_g",
  ];
  // A missing figure is an EMPTY cell, never a zero. A spreadsheet averages a
  // column, and a zero for "not measured" drags that average toward a value
  // nobody recorded — which is the same substitution the dash avoids on screen.
  const cell = (v: number | string | null) => (v === null ? "" : String(v));
  const lines = rows.map((r) =>
    [
      r.age_weeks, r.from.slice(0, 10), r.to.slice(0, 10), r.days_recorded, r.opening_birds,
      r.deaths, r.mortality_pct, r.eggs, r.hdp_pct, r.std_hdp_pct, r.hdp_gap,
      r.feed_per_bird_g, r.std_feed_per_bird_g, r.egg_weight_g, r.std_egg_weight_g,
      r.body_weight_g, r.std_body_weight_g,
    ].map(cell).join(","),
  );
  return [head.join(","), ...lines].join("\n");
}

function downloadCsv(csv: string, filename: string) {
  const blob = new Blob([csv], { type: "text/csv;charset=utf-8" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}
